import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  useVideoTemplatesContext,
  type VideoTemplateElementType,
} from './VideoTemplatesContext';

const logger = LogManager.getLogger('component:VideoTemplateSettings.queue');

const SAVE_DEBOUNCE_MS = 1000;

/** Delay before executing a toggle to yield to the browser for optimistic UI paint. */
const TOGGLE_YIELD_MS = 500;

export function useTemplateElementToggleQueue() {
  const {
    activeTemplateSettings,
    toggleTemplateElement,
    saveIfCurrentVideoLatest,
  } = useVideoTemplatesContext();

  // Each serialized toggle must bind to the freshly-committed context closure,
  // not the one captured when the queue was scheduled — the previous
  // toggle's setActiveTemplateSettings is what made it fresh.
  const toggleRef = useRef(toggleTemplateElement);
  useEffect(() => {
    toggleRef.current = toggleTemplateElement;
  }, [toggleTemplateElement]);

  const saveRef = useRef(saveIfCurrentVideoLatest);
  useEffect(() => {
    saveRef.current = saveIfCurrentVideoLatest;
  }, [saveIfCurrentVideoLatest]);

  // --- Pending state ---
  // Ref is the source of truth for processing; state drives optimistic UI.
  const pendingRef = useRef<Set<VideoTemplateElementType>>(new Set());
  const [pending, setPending] = useState<Set<VideoTemplateElementType>>(
    () => new Set(),
  );
  const [inFlight, setInFlight] = useState<VideoTemplateElementType | null>(
    null,
  );

  // Coalesces saves across a burst of toggles. `cancel()` is called just
  // before each toggle runs so a save can never fire mid-mutation; the
  // toggle's finally re-schedules.
  const debouncedSave = useMemo(
    () =>
      debounce(() => {
        void saveRef.current().catch(e => {
          logger.error('saveIfCurrentVideoLatest failed', {
            ...extractErrorInfo(e),
          });
        });
      }, SAVE_DEBOUNCE_MS),
    [],
  );

  // Flush any pending save when the hook unmounts so user toggles aren't
  // lost if the panel closes within the debounce window.
  useEffect(() => () => debouncedSave.flush(), [debouncedSave]);

  // --- Processing loop (imperative, no useEffect dispatch) ---
  const isProcessingRef = useRef(false);

  const processQueue = useCallback(async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    // Yield to browser so the optimistic state paints before heavy work.
    await new Promise(r => setTimeout(r, TOGGLE_YIELD_MS));

    // Drain items one at a time.
    while (pendingRef.current.size > 0) {
      const iter = pendingRef.current.values().next();
      if (iter.done) break;
      const type = iter.value;

      // Remove from ref + sync state
      pendingRef.current.delete(type);
      setPending(new Set(pendingRef.current));
      setInFlight(type);

      // Cancel any save scheduled by the previous toggle before mutating —
      // otherwise the save could fire mid-toggle and snapshot inconsistent state.
      debouncedSave.cancel();
      try {
        await toggleRef.current(type);
      } catch (e) {
        logger.error('toggleTemplateElement failed', {
          type,
          ...extractErrorInfo(e),
        });
      } finally {
        setInFlight(null);
        debouncedSave();
      }

      // Yield between items so UI updates between toggles.
      if (pendingRef.current.size > 0) {
        await new Promise(r => setTimeout(r, TOGGLE_YIELD_MS));
      }
    }

    isProcessingRef.current = false;
  }, [debouncedSave]);

  const queueToggle = useCallback(
    (type: VideoTemplateElementType) => {
      // Coalesce: toggling the same type twice cancels both.
      const set = pendingRef.current;
      if (set.has(type)) {
        set.delete(type);
      } else {
        set.add(type);
      }

      // Sync state for optimistic UI re-render.
      setPending(new Set(set));

      // Kick off processing (no-op if already running).
      void processQueue();
    },
    [processQueue],
  );

  const isEnabledOptimistic = useCallback(
    (type: VideoTemplateElementType): boolean => {
      const real =
        activeTemplateSettings?.videoTemplateElements.some(
          e => e.type === type,
        ) ?? false;
      const pendingFlip = pending.has(type) ? 1 : 0;
      const inFlightFlip = inFlight === type ? 1 : 0;
      return (pendingFlip + inFlightFlip) % 2 === 1 ? !real : real;
    },
    [activeTemplateSettings, pending, inFlight],
  );

  return { queueToggle, isEnabledOptimistic };
}
